import { useMutation } from '@tanstack/react-query';
import FeatherIcon from 'feather-icons-react';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';

import { updateNotificationEmail } from '../../../apis/queries';
import { isValidEmail } from '../../../helpers/validation';

interface NotificationEmailProps {
  onShowToast: (message: string, variant: 'success' | 'danger') => void;
  initialEmails?: string[];
}

const NotificationEmail = ({ onShowToast, initialEmails = [] }: NotificationEmailProps) => {
  const [emails, setEmails] = useState<string[]>(initialEmails.length ? initialEmails : ['']);
  const [isEditable, setIsEditable] = useState(false);

  const mutation = useMutation({
    mutationFn: (emails: string[]) => updateNotificationEmail({ emails }),
    onSuccess: () => {
      onShowToast('Notification email updated successfully!', 'success');
      setIsEditable(false);
    },
    onError: () => {
      onShowToast('Failed to update notification email. Please try again.', 'danger');
    }
  });

  useEffect(() => {
    if (emails.length === 0 || JSON.stringify(emails) !== JSON.stringify(initialEmails)) {
      setEmails(initialEmails.length ? initialEmails : ['']);
    }
  }, [initialEmails]);

  const onSaveNotificationEmail = () => {
    const validatedEmails = emails.filter(isValidEmail);
    mutation.mutate(validatedEmails);
  };

  const handleEmailChange = (index: number, value: string) => {
    const updatedEmails = [...emails];
    updatedEmails[index] = value.trim();
    setEmails(updatedEmails);
  };

  const addNewEmail = () => setEmails([...emails, '']);
  const removeEmail = (index: number) => {
    const updatedEmails = emails.filter((_, i) => i !== index);
    setEmails(updatedEmails.length ? updatedEmails : ['']);
  };

  const toggleEditMode = () => {
    setIsEditable(prev => !prev);
  };

  // const buttonStyle = { minWidth: '80px' };

  return (
    <Card className="py-4 px-4" style={{ boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)' }}>
      <Row>
        <Col xs={12}>
          <Form.Label className="mb-1">Notification Emails</Form.Label>
          <Form.Text className="small text-muted d-block mb-3">
            Email notifications will be sent to these adresses
          </Form.Text>
          {emails.map((email, index) => (
            <div key={index} className="d-flex align-items-center mb-2">
              <Form.Control
                type="email"
                placeholder="name@address.com"
                value={email}
                onChange={e => handleEmailChange(index, e.target.value)}
                isValid={isValidEmail(email)}
                readOnly={!isEditable}
                className="me-2"
              />
              {isEditable && (
                <span role="button" style={{ color: 'red', cursor: 'pointer' }} onClick={() => removeEmail(index)}>
                  <FeatherIcon icon="x" size="15" />
                </span>
              )}
            </div>
          ))}
          <div className="mt-4">
            {isEditable && (
              <Button variant="secondary" size="sm" onClick={addNewEmail} className="me-2" style={{ minWidth: '80px' }}>
                Add Email
              </Button>
            )}
            <Button
              size="sm"
              onClick={isEditable ? onSaveNotificationEmail : toggleEditMode}
              disabled={mutation.isPending}
              className="me-2"
              style={{ minWidth: '80px' }}
            >
              {mutation.isPending ? (
                <>
                  <Spinner animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Saving
                </>
              ) : isEditable ? (
                'Save'
              ) : (
                'Edit'
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </Card>
  );
};

export default NotificationEmail;
